import { map, get, find, isNil, findIndex } from 'lodash';
import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { BaseDebugContainer } from 'src/presentation/components/pages/integrations/lookupAdapterDebugging/components/LookupDebuggingStyles';
import { Button } from 'src/presentation/components/agent_desktop_components';
import lookupDebuggerBasicResource from 'src/actions/integrations/lookupAdapterDebugging/lookupDebuggerBasicResource';
import lookupDebuggerDetailedResource from 'src/actions/integrations/lookupAdapterDebugging/lookupDebuggerDetailedResource';
import RequestContainer from 'src/presentation/components/pages/integrations/lookupAdapterDebugging/components/RequestContainer';
import ResponseContainer, {
  ErrorCause,
  StyledErrorIcon,
  RowText,
} from 'src/presentation/components/pages/integrations/lookupAdapterDebugging/components/ResponseContainer';
import { Row } from 'src/presentation/components/forms/Layout';
import { Table, TR, THead, TBody, TH, TD } from '@ad-scripts/presentation/components/common/table';
import { useResourceValue } from 'src/presentation/hooks/useDocumentResource';

const BasicDebuggingResults = ({ selectedApp, orgId }): JSX.Element => {
  const { value: debugResults, loadingState, errors } = useResourceValue(lookupDebuggerBasicResource);
  const dispatch = useDispatch();
  const [selectedRowIdx, setSelectedRowIdx] = useState<number>();

  const rawResponse = debugResults?.response?.rawResponse && atob(debugResults.response.rawResponse);
  const results = debugResults?.response?.payload?.results;
  const searchResultAttr = selectedApp?.customerLookupSettings.searchResultAttributes;

  useEffect(() => {
    // reset the selected row whenever we fetch new debugResults
    if (loadingState === 'loading' || loadingState === 'unstarted') {
      setSelectedRowIdx(undefined);
    }
  }, [setSelectedRowIdx, loadingState]);

  const handleRowSelect = useCallback(
    idx => {
      setSelectedRowIdx(idx);
    },
    [setSelectedRowIdx]
  );

  const getRowData = result => {
    return map(searchResultAttr, attr => {
      if (attr.attr === 'email') {
        return (
          get(
            find(result.emails, email => !!email.primary),
            'original'
          ) ||
          get(result, 'emails[0].original') ||
          ''
        );
      } else if (attr.attr === 'phone') {
        return (
          get(
            find(result.phones, phone => !!phone.primary),
            'original'
          ) ||
          get(result, 'phones[0].original') ||
          ''
        );
      } else if (Object.prototype.hasOwnProperty.call(result, attr.attr)) {
        return result[attr.attr] || '';
      } else if (result.customAttributes && Object.prototype.hasOwnProperty.call(result.customAttributes, attr.attr)) {
        const value = result.customAttributes[attr.attr];
        return value || '';
      }
      return '';
    });
  };

  const getTableHeaders = () => {
    return map(searchResultAttr, attr => {
      return <TH key={`basicDebug-${attr.attr}`}>{attr.label}</TH>;
    });
  };

  const getTableData = () => {
    return map(results, (r, idx: number) => {
      const rowData = getRowData(r);

      return (
        <TR key={`basicDebug-row-${idx}`} onClick={() => handleRowSelect(idx)}>
          <TD key={`basicDebug-selector-${idx}`}>
            <Selector isSelected={selectedRowIdx === idx} />
          </TD>
          {map(rowData, (value, index) => (
            <TD data-aid={`basic-results-${index}`} key={`basic-results-${index}`}>
              {value}
            </TD>
          ))}
        </TR>
      );
    });
  };

  const handleSubmit = () => {
    if (!isNil(selectedRowIdx)) {
      const selectedResult = results[selectedRowIdx];

      const customerId = debugResults?.request?.payload?.query?.id;
      const integrationId = debugResults?.integrationId;
      const externalCustomerId = selectedResult?.externalCustomerId;

      if (!customerId || !integrationId || !externalCustomerId) {
        return;
      }

      dispatch(
        lookupDebuggerDetailedResource.effects.fetchDetailedDebugging(
          {
            orgId: orgId,
            integrationId: integrationId,
            customerId: customerId,
          },
          { externalCustomerId: externalCustomerId }
        )
      );
    }
  };

  const errorComponents = map(errors, (error, idx) => {
    let errorText;
    if (error.code === 'not_exist') {
      errorText = (
        <RowText>Customer not found in Gladly. Please try again with a different email or phone number.</RowText>
      );
    } else if (error.code === 'timeout') {
      errorText = <RowText>The request to the lookup adapter timed out.</RowText>;
    } else {
      errorText = <RowText>Something went wrong. Please try again.</RowText>;
    }

    return (
      <Row data-aid={`debug-error-${idx}`} key={`debug-error-${idx}`}>
        <StyledErrorIcon />
        {errorText}
      </Row>
    );
  });

  if (loadingState === 'unstarted') {
    return <></>;
  }

  const validationErrors = debugResults?.validationErrors;
  const shouldShowResults =
    results?.length > 0 && findIndex(validationErrors, { attr: ErrorCause.EXTERNAL_CUSTOMER_ID }) === -1;

  return (
    <>
      <StepContainer>Gladly is now testing the Basic Search workflow</StepContainer>
      <StepDivider />
      {loadingState === 'succeeded' ? (
        <>
          <RequestContainer request={debugResults?.request} requestType="Basic Search" />
          <ResponseContainer
            requestType="Basic Search"
            response={debugResults.response}
            rawResponse={rawResponse}
            validationErrors={validationErrors}
          />
          {shouldShowResults && (
            <BasicSearchResults>
              <b>Select a customer to continue the test</b>
              <StyledTable>
                <THead>
                  <TR>
                    <SelectorPlaceholder />
                    {getTableHeaders()}
                  </TR>
                </THead>
                <TBody>{getTableData()}</TBody>
              </StyledTable>
              <ButtonContainer>
                <Button data-aid="continue-test-button" disabled={isNil(selectedRowIdx)} onClick={handleSubmit}>
                  Continue
                </Button>
              </ButtonContainer>
            </BasicSearchResults>
          )}
        </>
      ) : null}
      {loadingState === 'failed' ? <DebugStepContainer>{errorComponents}</DebugStepContainer> : null}
    </>
  );
};

export default BasicDebuggingResults;

const BasicSearchResults = styled.div`
  ${BaseDebugContainer}
  margin: 12px 0 12px 24px;
`;

const ButtonContainer = styled.div`
  padding-top: 16px;
  display: flex;
  justify-content: flex-end;
`;

const DebugStepContainer = styled.div`
  ${BaseDebugContainer}
  margin: 12px 0 12px 24px;
`;

const SelectorPlaceholder = styled(TH)`
  width: 20px;
`;

const Selector = styled.div`
  border: 1px solid ${p => p.theme.colors.gray400};
  border-radius: 20px;
  height: 20px;
  width: 20px;
  ${p => p.isSelected && `background: ${p.theme.colors.green400}`};

  &:hover {
    background: ${p => (p.isSelected ? `${p => p.theme.colors.green400}` : `${p => p.theme.colors.green100}`)};
  }
`;

const StepDivider = styled.div`
  height: 16px;
  border-left: 4px dotted #767676;
  margin: 8px 24px;
`;

const StepContainer = styled.div`
  padding 0 24px;
`;

const StyledTable = styled(Table)`
  margin-top: 8px;
`;
